import { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import { getDeals } from '../services/api';
import type { Deal, DealStage } from '../types';

const stages: { key: DealStage; label: string; color: string }[] = [
  { key: 'discovery', label: 'Discovery', color: 'var(--info)' },
  { key: 'qualification', label: 'Qualification', color: 'var(--text-secondary)' },
  { key: 'proposal', label: 'Proposal', color: 'var(--warning)' },
  { key: 'negotiation', label: 'Negotiation', color: 'var(--netrun-green)' },
  { key: 'closed_won', label: 'Closed Won', color: 'var(--success)' },
];

// Map backend stage names to frontend stage keys
const stageMap: Record<string, DealStage> = {
  lead: 'discovery',
  qualified: 'qualification',
  discovery: 'discovery',
  qualification: 'qualification',
  proposal: 'proposal',
  negotiation: 'negotiation',
  closed_won: 'closed_won',
  closed_lost: 'closed_lost',
};

function DealCard({ deal, stageColor }: { deal: Deal; stageColor: string }) {
  return (
    <div
      className="rounded-lg p-4 cursor-pointer card-interactive"
      style={{
        background: 'var(--bg-input)',
        border: '1px solid var(--border-primary)',
        borderLeft: `3px solid ${stageColor}`,
      }}
    >
      <p className="text-sm font-body mb-1" style={{ color: 'var(--text-primary)' }}>
        {deal.title}
      </p>
      <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
        {deal.contact_name}{deal.organization_name ? ` -- ${deal.organization_name}` : ''}
      </p>
      <div className="flex items-center justify-between mb-2">
        <span className="font-display text-sm" style={{ color: 'var(--netrun-green)' }}>
          ${deal.value.toLocaleString()}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {deal.probability}%
        </span>
      </div>
      {/* Probability progress bar */}
      <div className="w-full h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${Math.min(deal.probability, 100)}%`,
            background: stageColor,
            opacity: 0.7,
          }}
        />
      </div>
    </div>
  );
}

export default function PipelinePage() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await getDeals({ limit: '100' });
        const items = data?.items ?? data ?? [];
        setDeals(Array.isArray(items) ? items.map((d: any) => ({
          id: d.id,
          title: d.name || d.title || 'Untitled',
          value: d.value || 0,
          stage: stageMap[d.stage] || d.stage || 'discovery',
          probability: d.probability ? Math.round(d.probability * 100) : 0,
          contact_id: d.primary_contact_id || d.contact_id || '',
          contact_name: d.contact_name || d.primary_contact_name || '',
          organization_name: d.organization_name || '',
          created_at: d.created_at || '',
          updated_at: d.updated_at || '',
        })) : []);
      } catch (err) {
        console.error('Failed to load deals:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const totalValue = deals.reduce((sum, d) => sum + d.value, 0);
  const weightedValue = deals.reduce((sum, d) => sum + d.value * (d.probability / 100), 0);

  if (loading) {
    return (
      <div className="page-enter">
        <Header title="Pipeline" subtitle="Loading deals..." />
        <div className="p-4 md:p-8 text-center" style={{ color: 'var(--text-muted)' }}>
          <p className="text-sm">Loading pipeline data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <Header
        title="Pipeline"
        subtitle={`${deals.length} deals across ${stages.length} stages`}
      />

      <div className="p-4 md:p-8">
        {/* Pipeline Summary Bar */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="rounded-xl p-4 accent-info"
            style={{
              background: 'rgba(17,17,17,0.85)',
              backdropFilter: 'blur(12px)',
              border: '1px solid var(--border-primary)',
            }}>
            <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Total Deals</p>
            <p className="text-2xl font-display" style={{ color: 'var(--text-primary)' }}>{deals.length}</p>
          </div>
          <div className="rounded-xl p-4 accent-sage"
            style={{
              background: 'rgba(17,17,17,0.85)',
              backdropFilter: 'blur(12px)',
              border: '1px solid var(--border-primary)',
            }}>
            <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Pipeline Value</p>
            <p className="text-2xl font-display" style={{ color: 'var(--netrun-green)' }}>
              ${totalValue.toLocaleString()}
            </p>
          </div>
          <div className="rounded-xl p-4 accent-warning"
            style={{
              background: 'rgba(17,17,17,0.85)',
              backdropFilter: 'blur(12px)',
              border: '1px solid var(--border-primary)',
            }}>
            <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Weighted Value</p>
            <p className="text-2xl font-display" style={{ color: 'var(--warning)' }}>
              ${Math.round(weightedValue).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Kanban board */}
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory md:snap-none pb-4">
          {stages.map((stage) => {
            const stageDeals = deals.filter(d => d.stage === stage.key);
            const stageValue = stageDeals.reduce((sum, d) => sum + d.value, 0);

            return (
              <div key={stage.key} className="flex-shrink-0 w-[280px] snap-center md:snap-align-none">
                {/* Stage header */}
                <div className="flex items-center justify-between mb-4 px-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: stage.color }} />
                    <span className="text-sm font-body" style={{ color: 'var(--text-primary)' }}>
                      {stage.label}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-display"
                      style={{
                        background: `color-mix(in srgb, ${stage.color} 15%, transparent)`,
                        color: stage.color,
                      }}>
                      {stageDeals.length}
                    </span>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    ${stageValue.toLocaleString()}
                  </span>
                </div>

                {/* Deal cards - glassmorphism column */}
                <div className="space-y-3 min-h-[200px] rounded-xl p-3"
                  style={{
                    background: 'rgba(17,17,17,0.85)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid var(--border-primary)',
                    borderTop: `3px solid ${stage.color}`,
                  }}>
                  {stageDeals.map((deal) => (
                    <DealCard key={deal.id} deal={deal} stageColor={stage.color} />
                  ))}
                  {stageDeals.length === 0 && (
                    <p className="text-xs text-center py-8" style={{ color: 'var(--text-muted)' }}>
                      No deals
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
